/**
 * Svea settlement reads (#2811).
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface UnknownSettledPayment {
  paymentId: string;
  orderNumber: string;
  sellerGrossCents: number;
  sellerNetCents: number;
  refundedCents: number;
  commissionCents: number;
  compensationDate: string;
  paymentMethod: string | null;
}

export interface PaidUnsettledPayment {
  pmtId: string;
  invoiceId: string;
  amountCents: number;
  status: string;
  updatedAt: string;
}

export interface SveaSettlementExceptions {
  unknownSettled: UnknownSettledPayment[];
  paidUnsettled: PaidUnsettledPayment[];
  unsettledAfterDays: number;
}

export interface SveaInvoiceSettlementPayment {
  pmtId: string;
  status: string;
  settled: boolean;
  settlementDate: string | null;
  commissionCents: number | null;
  commissionVatCents: number | null;
  sellerGrossCents: number | null;
  sellerNetCents: number | null;
  refundedCents: number | null;
}

export interface SveaInvoiceSettlementSummary {
  invoiceId: string;
  settled: boolean;
  settlementDate: string | null;
  commissionCents: number;
  payments: SveaInvoiceSettlementPayment[];
}

export const sveaSettlementExceptionQueryKey = ['svea-settlements', 'exceptions'] as const;

export function sveaInvoiceSettlementQueryKey(invoiceId: string) {
  return ['svea-settlements', 'invoices', invoiceId] as const;
}

export function useSveaSettlementExceptions(enabled = true) {
  return useQuery({
    queryKey: sveaSettlementExceptionQueryKey,
    queryFn: () => api.get<SveaSettlementExceptions>('/svea-settlements/exceptions'),
    enabled,
  });
}

export function useSveaInvoiceSettlement(invoiceId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: sveaInvoiceSettlementQueryKey(invoiceId ?? ''),
    queryFn: () => api.get<SveaInvoiceSettlementSummary>(`/svea-settlements/invoices/${invoiceId}`),
    enabled: enabled && Boolean(invoiceId),
  });
}
